import * as React from 'react'
import { useTranslations } from 'next-intl'
import { Divider } from 'components/divider'
import { Tooltip } from 'primitives/tooltip'
import { QuestionCircleIcon } from 'primitives/icons/general'
import { useRefinementList } from 'react-instantsearch'
import { tw } from 'utils/classnames'
import { Checkbox } from 'primitives/cf-checkbox'

import { SearchInput } from '../search-input'
import { SidebarFilter } from './search-sidebar-filter'

enum ClassSpecificSearchableFilters {
  ISFREE = 'isFree',
  LEVEL = 'extraSettings.level',
  SOURCE = 'extraSettings.source',
  CATEGORY = 'category',
}

export function ClassSearchSidebar() {
  const t = useTranslations('pages.search')

  return (
    <div className="bg-cf-white rounded">
      <h3 className="p-5 text-sm font-bold">{t('filter.search_and_filter')}</h3>
      <Divider className="h-[3px] bg-gray-700" />
      <div className="flex flex-col gap-5 p-5">
        <SearchInput />
        <Divider className="relative left-[-20px] w-[calc(100%+40px)] bg-gray-700" />
        <ClassIsFreeMenu />
        <Divider className="relative left-[-20px] w-[calc(100%+40px)] bg-gray-700" />
        <SidebarFilter
          attribute={`${ClassSpecificSearchableFilters.LEVEL}`}
          header={t('classes.class_level')}
        />
        <SidebarFilter
          attribute={`${ClassSpecificSearchableFilters.SOURCE}`}
          header={t('classes.class_creative_fabrica')}
        >
          {({ items, handleRefine }) => (
            <div className="mt-5 flex flex-col">
              {items.map((item, index) => {
                const cfOriginal = item.value === 'Original'

                return (
                  <div key={item.label + index} className="wrap flex items-center">
                    <Checkbox
                      label={
                        <div className="flex items-center gap-[3px]">
                          <span className={tw(item.isRefined && 'font-semibold', 'capitalize')}>
                            {cfOriginal
                              ? t('classes.class_cf_original')
                              : t('classes.class_cf_sourced')}
                          </span>
                          <Tooltip
                            delay={0}
                            sideOffset={5}
                            side="top"
                            className="bg-secondary-500 w-[250px]"
                            arrowClassName="fill-secondary-500"
                            value={
                              cfOriginal
                                ? t('tooltips.class_tooltip_cf_original')
                                : t('tooltips.class_tooltip_cf_sourced')
                            }
                          >
                            <button>
                              <QuestionCircleIcon className="size-[14px] text-gray-300" />
                            </button>
                          </Tooltip>
                          <span className="text-xs text-gray-500">({item.count})</span>
                        </div>
                      }
                      value={item.value}
                      checked={item.isRefined}
                      onClick={() => handleRefine(item.value)}
                      className="!mr-0"
                    />
                  </div>
                )
              })}
            </div>
          )}
        </SidebarFilter>
        <SidebarFilter
          attribute={`${ClassSpecificSearchableFilters.CATEGORY}`}
          header={t('filter.categories')}
          hideEndDivider={true}
        />
      </div>
    </div>
  )
}

enum ClassFilter {
  ALL = 'all',
  FREE = 'free',
  PAID = 'paid',
}

export const BUTTON_FILTERS = [ClassFilter.ALL, ClassFilter.FREE, ClassFilter.PAID]

function ClassIsFreeMenu() {
  const { refine, items } = useRefinementList({ attribute: 'isFree' })
  const t = useTranslations('pages.search')

  const isFree = items.find(item => item.isRefined)?.value
  const currentFilter =
    // oxlint-disable-next-line no-nested-ternary
    isFree === 'true' ? ClassFilter.FREE : isFree === 'false' ? ClassFilter.PAID : ClassFilter.ALL

  const [isActive, setIsActive] = React.useState(currentFilter)

  const handleButtonClick = (filter: ClassFilter) => {
    if (isActive === filter) {
      return
    }

    items?.forEach(item => {
      if (item.isRefined) {
        refine(item.value)
      }
    })
    setIsActive(filter)

    if (filter === ClassFilter.ALL) {
      return
    }

    refine(filter === ClassFilter.FREE ? 'true' : 'false')
  }

  return (
    <div className="flex justify-evenly gap-[10px]">
      {BUTTON_FILTERS.map(filter => (
        <button
          onClick={() => handleButtonClick(filter)}
          className={tw(
            isActive === filter ? 'bg-[#1e3892]' : 'bg-[#0177FB]',
            'text-cf-white h-[30px] w-full rounded font-semibold uppercase hover:bg-[#1e3892]',
          )}
          key={filter}
        >
          {t(`classes.search_${filter}`)}
        </button>
      ))}
    </div>
  )
}
